import React, { useEffect, useState } from "react";
import { Card, CardBody, CardHeader, Badge, Table, Row, Col } from "reactstrap";
import requests from "../requests";

interface ScorecardCall {
  ticker: string;
  direction: "up" | "down";
  window: number;
  conviction: number;
  return: number;
  correct: boolean;
}

// No hit_rate / avg_return: the API stops sending aggregate accuracy rates (2026-07-31).
interface ScorecardFull {
  n_signals: number;
  n_pending: number;
  bull_count: number;
  bear_count: number;
}

interface Scorecard {
  week_start: string;
  week_end: string;
  headline: ScorecardCall[];
  full: ScorecardFull;
  backfilled?: boolean;
}

const fmtDate = (iso: string) =>
  new Date(`${iso}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric" });

// Weekly receipts: the top-conviction calls each week and how they actually
// resolved (wins AND misses). Deliberately shows NO aggregate hit rate — the
// 2026-07-31 audit found no edge clearing the matched null, so an accuracy
// percentage would over-claim (the API no longer sends one either). The per-call
// ✓/✗ rows stay: they are what happened, and a reader can tally them.
// Renders nothing until /api/scorecards has data, so it's safe to always mount.
// `limit` trims to the most recent N weeks for a compact preview (e.g. the homepage
// transparency band); omit it on the full Track Record page to show every week.
export default function WeeklyScorecards({ limit }: { limit?: number } = {}) {
  const [cards, setCards] = useState<Scorecard[] | null>(null);

  useEffect(() => {
    requests.get("/api/scorecards")
      .then((r) => setCards(Array.isArray(r.data) ? (r.data as Scorecard[]) : []))
      .catch(() => setCards([]));
  }, []);

  if (!cards || cards.length === 0) return null;

  // For a preview, show the most recent `limit` weeks (sort desc by start, then slice)
  // so the homepage always leads with the freshest receipts regardless of API ordering.
  const shown = limit
    ? [...cards].sort((a, b) => b.week_start.localeCompare(a.week_start)).slice(0, limit)
    : cards;

  return (
    <Card>
      <CardHeader style={{ borderBottom: "1px solid var(--border-color)", background: "var(--card-background)" }}>
        <h5 className="mb-0" style={{ fontWeight: 700 }}>Weekly Scorecards</h5>
      </CardHeader>
      <CardBody>
        <p className="text-muted mb-3" style={{ fontSize: 14 }}>
        Our highest-conviction calls each week and how they actually resolved — wins and misses,
        published either way. Each card also shows how many signals we put out that week, so
        nothing is cherry-picked.
        </p>
        <Row>
          {shown.map((c) => (
            <Col lg="6" key={c.week_start} className="mb-4">
              <Card className="h-100">
                <CardHeader className="d-flex justify-content-between align-items-center">
                  <strong>{fmtDate(c.week_start)} – {fmtDate(c.week_end)}</strong>
                  <span>
                    {c.backfilled && <Badge color="light" className="text-muted ms-2">backfilled</Badge>}
                  </span>
                </CardHeader>
                <CardBody>
                  <Table size="sm" borderless className="mb-2 align-middle">
                    <tbody>
                      {c.headline.map((h, i) => (
                        <tr key={`${h.ticker}_${i}`}>
                          <td className="fw-bold">{h.ticker}</td>
                          <td>
                            <Badge color={h.direction === "up" ? "success" : "danger"}>
                              {h.direction === "up" ? "Bull" : "Bear"}
                            </Badge>
                          </td>
                          <td className="text-muted" style={{ fontSize: 13 }}>{h.conviction.toFixed(0)}% conv</td>
                          <td className={`text-end ${h.return >= 0 ? "text-success" : "text-danger"}`}>
                            {h.return >= 0 ? "+" : ""}{h.return.toFixed(1)}%
                          </td>
                          <td className="text-end" style={{ width: 24 }}>{h.correct ? "✓" : "✗"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                  <div className="text-muted" style={{ fontSize: 12 }}>
                  Full week: {c.full.n_signals} signals · {c.full.bull_count} bull / {c.full.bear_count} bear
                  </div>
                </CardBody>
              </Card>
            </Col>
          ))}
        </Row>
      </CardBody>
    </Card>
  );
}
